import { GetSportIcon } from 'assets/icons';
import cn from 'classnames';
import sortBy from 'lodash/sortBy';
import { Dispatch, memo, SetStateAction, useCallback, useEffect } from 'react';
import { useActivate } from 'react-activation';
import ScrollContainer from 'react-indiana-drag-scroll';
import { useSelector } from 'react-redux';
import { useScrollToTab } from 'shared/hooks/useScrollToTab';
import { CategoryDataInterface, SportsType, State } from 'shared/interface';

interface HomeTabsProps {
	tabList: SportsType[];
	currentTab: string | number | undefined;
	onTabChange: (tab: SportsType) => void;
	categorySwitched?: boolean;
	cyTestRef?: string;
	filteredSubCategories?: CategoryDataInterface[];
	setFilteredSubcategories?: Dispatch<SetStateAction<CategoryDataInterface[]>>;
	setCurrentSubCategory?: Dispatch<SetStateAction<number | null>>;
}

export const SportTabs = memo(
	({
		tabList,
		currentTab,
		categorySwitched,
		onTabChange,
		cyTestRef,
		filteredSubCategories = [],
		setFilteredSubcategories,
		setCurrentSubCategory,
	}: HomeTabsProps) => {
		const sortCategories = (categories: CategoryDataInterface[]) => {
			return sortBy(categories, ['sortOrder']) || [];
		};
		const categoriesPopular = useSelector<State, CategoryDataInterface[]>((state) => sortCategories(state.categories.popular));

		const { scrollContainerRef, tabRefs, scrollToTab } = useScrollToTab({ useOffset: true });
		const item = tabList.find((item) => item.sportId === currentTab);

		useEffect(() => {
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			onTabChange(tabList[0]!);
			if (categorySwitched) {
				setFilteredSubcategories && setFilteredSubcategories([]);
				setCurrentSubCategory && setCurrentSubCategory(null);
			} else {
				subCategories();
			}
		}, [categorySwitched, tabList.length]);

		const subCategories = useCallback(() => {
			if (currentTab !== filteredSubCategories[0]?.sportId) {
				const currentTabSubcategories = categoriesPopular?.filter((item) => item.sportId === currentTab) || [];
				setFilteredSubcategories && setFilteredSubcategories(currentTabSubcategories);
			}
		}, [categoriesPopular, currentTab, filteredSubCategories]);

		const restoreTabScroll = () => {
			if (item) {
				scrollToTab(tabList.indexOf(item));
			}
		};

		useEffect(() => {
			if (currentTab !== filteredSubCategories[0]?.sportId) {
				subCategories();
			}
			restoreTabScroll();
		}, [currentTab]);

		useActivate(() => {
			restoreTabScroll();
		});

		const onTabClick = (tab: SportsType, index: number) => {
			if (currentTab !== tab.sportId) {
				onTabChange(tab);
			}
		};

		return (
			<div className="relative overflow-hidden">
				<div className="w-4 h-10 z-1 absolute left-0 bottom-4 bg-gradient-to-l from-transparent to-primary-50 dark:to-dark-100" />
				<div className="w-4 h-10 z-1 absolute right-0 bottom-4 bg-gradient-to-r from-transparent to-primary-50 dark:to-dark-100" />
				<ScrollContainer className="tabs_wrapper" ref={scrollContainerRef}>
					<div className="HomeTabs flex gap-2 w-fit overflow-x-auto overflow-y-hidden" style={{ padding: '6px 0px' }} data-cy={cyTestRef}>
						{tabList.map((tab, index) => (
							<div
								ref={(el) => (tabRefs.current[index] = el)}
								className={cn(
									'relative w-auto h-12 px-4 cursor-pointer overflow-hidden',
									'text-paragraph-sm capitalize font-medium whitespace-nowrap text-ellipsis',
									'flex items-center gap-2',
									'border-b-3',
									currentTab === tab.sportId ? 'border-primary-500 dark:border-gray-100' : 'border-b-transparent',
									{
										'text-primary-500 dark:text-gray-100': currentTab === tab.sportId,
									},
								)}
								onClick={() => onTabClick(tab, index)}
								key={`marketTypes-pill-${index}`}
							>
								<div className="flex">
									{tab.sportName === 'Hot' ? <span>🔥</span> : <GetSportIcon id={tab.sportId} className="w-4.5 h-4.5" />}
								</div>
								<div>{tab.sportName}</div>
							</div>
						))}
					</div>
				</ScrollContainer>
			</div>
		);
	},
);
